import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    createVirtualAppointment,
    type VirtualAppointmentPlan
} from '../../services/virtualAppointments';
import { PATIENT_VIRTUAL_APPOINTMENTS_KEY } from '../../hooks/queryKeys';
import { apiErrorMessage } from '../../utils/apiError';

const CREATE_FALLBACK = "Couldn't add this VA. Try again.";

export interface CreateVirtualAppointment {
    submitting: boolean;
    error: string | null;
    create: (plan: VirtualAppointmentPlan, onSuccess: () => void) => void;
    resetError: () => void;
}

// Waits on the list refetch before reporting success, so the new VA is listed when the modal closes.
export function useCreateVirtualAppointment(patientId: string): CreateVirtualAppointment {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: (plan: VirtualAppointmentPlan) => createVirtualAppointment(patientId, plan),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: PATIENT_VIRTUAL_APPOINTMENTS_KEY(patientId)
            })
    });
    const { mutate, reset, isPending, isError, error } = mutation;
    const create = useCallback(
        (plan: VirtualAppointmentPlan, onSuccess: () => void) => mutate(plan, { onSuccess }),
        [mutate]
    );
    const message = isError ? apiErrorMessage(error, CREATE_FALLBACK) : null;
    return useMemo(
        () => ({ submitting: isPending, error: message, create, resetError: reset }),
        [isPending, message, create, reset]
    );
}
